import { Container, Graphics } from '../../../vendor/pixi.min.mjs';
import {
    PIPELINE_CONFIG, DIR_DELTA, OPPOSITE, getExitDir,
} from './pipelineConfig.js';
import { drawTramCarRect } from '../../ui/TramCar.js';

const { POINTS_PER_TILE, POINTS_CROSS_BONUS, STATION_POINTS } = PIPELINE_CONFIG;

/**
 * Train that moves smoothly through placed track tiles.
 * Returns status flags: alive, reachedEnd, visitedStation.
 */
export class PipelineTrain {
    gfx = new Container();

    _col = 0;
    _row = 0;
    _prevCol = 0;
    _prevRow = 0;
    _nextCol = 0;
    _nextRow = 0;
    _lookaheadCol = 0;
    _lookaheadRow = 0;
    _entryDir = 3;
    _exitDir = 1;
    _progress = 0;
    _started = false;

    _speed = 0;
    _baseSpeed = 0;
    _maxSpeed = 0;
    _speedIncrement = 0;

    tilesPassed = 0;
    score = 0;
    stationsVisited = 0;

    _cellSize = 0;
    _headGfx = new Graphics();
    _tailGfx = new Graphics();
    _jointGfx = new Graphics();
    _trail = [];
    _themeManager = null;

    constructor(cellSize, themeManager = null) {
        this._cellSize = cellSize;
        this._themeManager = themeManager;
        this._drawTramCar(this._headGfx, 'pipeline.tram.head');
        this._drawTramCar(this._tailGfx, 'pipeline.tram.body');
        this._headGfx.zIndex = 2;
        this._jointGfx.zIndex = 1;
        this._tailGfx.zIndex = 1;
        this.gfx.addChild(this._jointGfx);
        this.gfx.addChild(this._tailGfx);
        this.gfx.addChild(this._headGfx);
        this.gfx.sortableChildren = true;
        this.gfx.zIndex = 100;
        this.gfx.visible = false;
    }

    init(startCol, startRow, baseSpeed, maxSpeed, speedIncrement) {
        this._col = startCol;
        this._row = startRow;
        this._baseSpeed = baseSpeed;
        this._maxSpeed = maxSpeed;
        this._speedIncrement = speedIncrement;
        this._speed = baseSpeed;
        this._progress = 0;
        this._started = false;
        this.tilesPassed = 0;
        this.score = 0;
        this.stationsVisited = 0;
        this._trail = [];
    }

    /**
     * @param {import('./PipelineBoard.js').PipelineBoard} board
     * @returns {boolean}
     */
    start(board) {
        const tile = board.getTile(this._col, this._row);
        if (!tile) return false;

        this._entryDir = 3;
        if (!tile.connections[this._entryDir]) return false;

        const markResult = board.markPassed(this._col, this._row);
        this.tilesPassed = 1;
        this.score = POINTS_PER_TILE + (tile.type === 'cross' ? POINTS_CROSS_BONUS : 0);

        if (markResult.station) {
            this.score += STATION_POINTS;
            this.stationsVisited++;
        }

        const exitDir = getExitDir(tile.connections, this._entryDir);
        if (exitDir === -1) return false;

        this._exitDir = exitDir;
        this._setPathState(board, this._col, this._row, this._entryDir, exitDir);
        this._progress = 0;
        this._started = true;
        this.gfx.visible = true;
        const startPos = this._sampleSegment(0);
        this._trail = [startPos];

        return true;
    }

    /**
     * @returns {{ alive: boolean, reachedEnd: boolean, visitedStation: boolean }}
     */
    update(delta, board) {
        if (!this._started) return { alive: true, reachedEnd: false, visitedStation: false };

        this._progress += (this._speed / 60) * delta;

        if (this._progress < 1) return { alive: true, reachedEnd: false, visitedStation: false };

        this._progress -= 1;
        this._col = this._nextCol;
        this._row = this._nextRow;

        if (this._col < 0 || this._col >= board.cols ||
            this._row < 0 || this._row >= board.rows) {
            return { alive: false, reachedEnd: false, visitedStation: false };
        }

        if (board.isEnd(this._col, this._row)) {
            return { alive: true, reachedEnd: true, visitedStation: false };
        }

        const tile = board.getTile(this._col, this._row);
        if (!tile) return { alive: false, reachedEnd: false, visitedStation: false };

        this._entryDir = OPPOSITE[this._exitDir];
        if (!tile.connections[this._entryDir]) {
            return { alive: false, reachedEnd: false, visitedStation: false };
        }

        const markResult = board.markPassed(this._col, this._row);
        this.tilesPassed++;
        this.score += POINTS_PER_TILE + (tile.type === 'cross' ? POINTS_CROSS_BONUS : 0);

        let visitedStation = false;
        if (markResult.station) {
            this.score += STATION_POINTS;
            this.stationsVisited++;
            visitedStation = true;
        }

        const exitDir = getExitDir(tile.connections, this._entryDir);
        if (exitDir === -1) {
            return { alive: false, reachedEnd: false, visitedStation };
        }

        this._exitDir = exitDir;
        this._setPathState(board, this._col, this._row, this._entryDir, exitDir);

        const dx = Math.abs(this._col - board.endCol);
        const dy = Math.abs(this._row - board.endRow);
        const distToEnd = dx + dy;
        if (distToEnd <= 8) {
            const boost = 1 + (8 - distToEnd) * 0.25;
            this._speed = Math.max(this._speed, this._maxSpeed * boost);
        } else {
            this._speed = Math.min(
                this._maxSpeed,
                this._baseSpeed + this.tilesPassed * this._speedIncrement,
            );
        }

        return { alive: true, reachedEnd: false, visitedStation };
    }

    render() {
        if (!this._started) {
            const cs = this._cellSize;
            this.gfx.x = this._col * cs + cs / 2;
            this.gfx.y = this._row * cs + cs / 2;
            return;
        }

        // Once movement begins, car positions are expressed in board-local
        // coordinates, so the parent container must sit at the origin.
        this.gfx.x = 0;
        this.gfx.y = 0;

        const headPos = this._sampleSegment(this._progress);
        this._pushTrailPoint(headPos);

        const carSpacing = this._cellSize * 1.28;
        const headAhead = this._sampleTrail(12);
        const tailPos = this._sampleTrail(carSpacing);
        const tailAhead = this._sampleTrail(carSpacing + 12);
        const dx = headPos.x - tailPos.x;
        const dy = headPos.y - tailPos.y;
        const jointAngle = Math.atan2(dy, dx);
        const headBackX = headPos.x - Math.cos(jointAngle) * this._cellSize * 0.48;
        const headBackY = headPos.y - Math.sin(jointAngle) * this._cellSize * 0.48;
        const tailFrontX = tailPos.x + Math.cos(jointAngle) * this._cellSize * 0.48;
        const tailFrontY = tailPos.y + Math.sin(jointAngle) * this._cellSize * 0.48;

        this._jointGfx.clear();
        this._jointGfx.removeChildren();
        const connectorLen = Math.max(12, Math.hypot(headBackX - tailFrontX, headBackY - tailFrontY));
        const connectorSprite = this._themeManager?.createSprite('pipeline.tram.connector', connectorLen, this._cellSize * 0.22, {
            fitMode: 'cover',
        });
        if (connectorSprite) {
            connectorSprite.x = (tailFrontX + headBackX) / 2;
            connectorSprite.y = (tailFrontY + headBackY) / 2;
            connectorSprite.rotation = jointAngle;
            this._jointGfx.addChild(connectorSprite);
        } else {
            this._jointGfx.moveTo(tailFrontX, tailFrontY);
            this._jointGfx.lineTo(headBackX, headBackY);
            this._jointGfx.stroke({ color: 0x334155, width: Math.max(4, Math.round(this._cellSize * 0.1)) });
            this._jointGfx.circle(tailFrontX, tailFrontY, Math.max(4, Math.round(this._cellSize * 0.08)));
            this._jointGfx.fill(0x475569);
            this._jointGfx.circle(headBackX, headBackY, Math.max(4, Math.round(this._cellSize * 0.08)));
            this._jointGfx.fill(0x475569);
        }

        this._headGfx.x = headPos.x;
        this._headGfx.y = headPos.y;
        this._headGfx.rotation = Math.atan2(headPos.y - headAhead.y, headPos.x - headAhead.x);

        this._tailGfx.x = tailPos.x;
        this._tailGfx.y = tailPos.y;
        this._tailGfx.rotation = Math.atan2(tailPos.y - tailAhead.y, tailPos.x - tailAhead.x);
    }

    destroy() {
        if (this.gfx.parent) this.gfx.parent.removeChild(this.gfx);
        this.gfx.destroy({ children: true });
    }

    _setPathState(board, col, row, entryDir, exitDir) {
        this._prevCol = col + DIR_DELTA[entryDir].dc;
        this._prevRow = row + DIR_DELTA[entryDir].dr;
        this._nextCol = col + DIR_DELTA[exitDir].dc;
        this._nextRow = row + DIR_DELTA[exitDir].dr;

        const lookahead = this._computeLookahead(board, this._nextCol, this._nextRow, OPPOSITE[exitDir], exitDir);
        this._lookaheadCol = lookahead.col;
        this._lookaheadRow = lookahead.row;
    }

    _computeLookahead(board, col, row, entryDir, fallbackDir) {
        if (col < 0 || col >= board.cols || row < 0 || row >= board.rows || board.isEnd(col, row)) {
            return {
                col: col + DIR_DELTA[fallbackDir].dc,
                row: row + DIR_DELTA[fallbackDir].dr,
            };
        }

        const tile = board.getTile(col, row);
        if (!tile || !tile.connections[entryDir]) {
            return {
                col: col + DIR_DELTA[fallbackDir].dc,
                row: row + DIR_DELTA[fallbackDir].dr,
            };
        }

        const exitDir = getExitDir(tile.connections, entryDir);
        if (exitDir === -1) {
            return {
                col: col + DIR_DELTA[fallbackDir].dc,
                row: row + DIR_DELTA[fallbackDir].dr,
            };
        }

        return {
            col: col + DIR_DELTA[exitDir].dc,
            row: row + DIR_DELTA[exitDir].dr,
        };
    }

    _sampleSegment(t) {
        const cs = this._cellSize;
        const p0 = this._tileCenter(this._prevCol, this._prevRow, cs);
        const p1 = this._tileCenter(this._col, this._row, cs);
        const p2 = this._tileCenter(this._nextCol, this._nextRow, cs);
        const p3 = this._tileCenter(this._lookaheadCol, this._lookaheadRow, cs);
        return {
            x: this._catmull(p0.x, p1.x, p2.x, p3.x, t),
            y: this._catmull(p0.y, p1.y, p2.y, p3.y, t),
        };
    }

    _tileCenter(col, row, cs) {
        return { x: col * cs + cs / 2, y: row * cs + cs / 2 };
    }

    _catmull(p0, p1, p2, p3, t) {
        const t2 = t * t;
        const t3 = t2 * t;
        return 0.5 * (
            (2 * p1) +
            (-p0 + p2) * t +
            (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 +
            (-p0 + 3 * p1 - 3 * p2 + p3) * t3
        );
    }

    _pushTrailPoint(point) {
        const last = this._trail[0];
        if (!last) {
            this._trail.unshift(point);
            return;
        }
        const dx = point.x - last.x;
        const dy = point.y - last.y;
        if ((dx * dx + dy * dy) < 4) return;
        this._trail.unshift(point);
        if (this._trail.length > 120) this._trail.length = 120;
    }

    _sampleTrail(distanceBack) {
        if (this._trail.length === 0) return { x: 0, y: 0 };
        if (distanceBack <= 0) return this._trail[0];

        let remaining = distanceBack;
        for (let i = 0; i < this._trail.length - 1; i++) {
            const a = this._trail[i];
            const b = this._trail[i + 1];
            const dx = b.x - a.x;
            const dy = b.y - a.y;
            const dist = Math.hypot(dx, dy);
            if (dist <= 0.001) continue;
            if (remaining <= dist) {
                const t = remaining / dist;
                return {
                    x: a.x + dx * t,
                    y: a.y + dy * t,
                };
            }
            remaining -= dist;
        }
        return this._trail[this._trail.length - 1];
    }

    _drawTramCar(target, slotKey) {
        const cs = this._cellSize;
        const w = Math.round(cs * 1.18);
        const h = Math.round(cs * 0.58);

        target.clear();
        target.removeChildren();

        const sprite = this._themeManager?.createSprite(slotKey, w, h);
        if (sprite) {
            sprite.x = 0;
            sprite.y = 0;
            target.addChild(sprite);
            return;
        }

        drawTramCarRect(target, { x: -w / 2, y: -h / 2, w, h }, {
            horizontal: true,
            forwardSign: 1,
            windows: 3,
        });
    }
}
